import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useUrlHash } from '@client/router/useUrlHash';

type UseTabHash<T extends string> = {
  tabHash: T;
  setTabHash: (v: T) => void;
  onTabChange: (_: React.SyntheticEvent | null, v: T) => void;
};

export const useTabHash = <T extends string>(
  tabs: ReadonlyArray<T>,
  defaultValue: T,
  canLeaveTab: () => boolean = () => true,
): UseTabHash<T> => {
  const [hash, setHash] = useUrlHash();
  const isTab = (v: string): v is T => tabs.includes(v as T);
  const [tabHash, setShownTab] = useState<T>(isTab(hash) ? hash : defaultValue);

  const latest = useRef({ isTab, tabHash, canLeaveTab });
  useLayoutEffect(() => {
    latest.current = { isTab, tabHash, canLeaveTab };
  });

  useEffect(() => {
    const shown = latest.current;
    if (hash === shown.tabHash) return;
    if (shown.isTab(hash) && shown.canLeaveTab()) setShownTab(hash);
    else setHash(shown.tabHash, { replace: true });
  }, [hash, setHash]);

  const setTabHash = (v: T) => {
    setShownTab(v);
    setHash(v, { replace: true });
  };

  return {
    tabHash,
    setTabHash,
    onTabChange: (_: React.SyntheticEvent | null, v: T) => setTabHash(v),
  };
};

const getStoredTab = <T>(tabs: Array<T>, def: T, storageKey: string): T => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const startKey: any = localStorage.getItem(storageKey);
  return tabs.includes(startKey) ? startKey : def;
};

export const usePersistedTabHash = <T extends string>(
  tabs: Array<T>,
  defaultValue: T,
  storageKey: string,
): UseTabHash<T> => {
  const storedValue = getStoredTab(tabs, defaultValue, storageKey);
  const t = useTabHash(tabs, storedValue);
  const setTabHash = (v: T) => {
    t.setTabHash(v);
    localStorage.setItem(storageKey, v);
  };
  return {
    tabHash: t.tabHash,
    setTabHash,
    onTabChange: (_, v) => setTabHash(v),
  };
};
